import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { Box, Paper } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import PageContainer from "../../common_components/PageContainer";
import Pagination from "../../common_components/Pagination";
import ConfirmActionModal from "../../common_components/ConfirmActionModal";
import NotesModal from "../../common_components/NotesModal";
import { useLayout } from "../../contexts/LayoutContext";
import EmailConfigHeader from "./Component/EmailConfigHeader";
import EmailConfigList from "./Component/EmailConfigList";
import AddEditDrawer from "./Drawer/AddEditDrawer";
import UserAssignmentDrawer from "./Drawer/UserAssignDrawer";
import { fetchEmailConfigurationBootstrap, fetchUserRecipientRoles, createEmailConfiguration, updateEmailConfiguration, bulkUpdateEmailConfigurations, deleteEmailConfiguration, selectEmailConfigurations, selectEmailConfigModules, selectEmailConfigUsers, selectEmailConfigurationLoading, selectEmailConfigurationSaving, } from "../../redux/emailConfigurationSlice";
import { EMAIL_CONFIG_PAGINATION, buildModuleNameMap, buildFilterModuleNameMap, resolveModuleDisplayName, getSenderLabel, formatUserNames, } from "./utils";
// Client-side filter + sort
const applySearchAndSort = (data, sortConfig, searchText, moduleNameMap) => {
    const search = searchText?.trim().toLowerCase();
    let result = [...data];
    if (search) {
        result = result.filter((config) => {
            const searchableText = [
                config.notification_key,
                resolveModuleDisplayName(config, moduleNameMap),
                config.subject,
                config.sender,
                getSenderLabel(config.sender),
                config.default_recipient?.join(', ') || '',
                formatUserNames(config.additional_user_ids),
                formatUserNames(config.cc),
                formatUserNames(config.bcc),
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return searchableText.includes(search);
        });
    }
    if (sortConfig?.columnId && sortConfig.direction) {
        const { columnId, direction } = sortConfig;
        const getSortValue = (config) => {
            if (columnId === 'module')
                return resolveModuleDisplayName(config, moduleNameMap);
            if (columnId === 'notificationKey')
                return config.notification_key;
            if (columnId === 'defaultRecipients')
                return config.default_recipient?.join(', ') || '';
            if (columnId === 'receiver')
                return getSenderLabel(config.sender);
            return config[columnId];
        };
        result.sort((a, b) => {
            const av = getSortValue(a);
            const bv = getSortValue(b);
            if (av == null)
                return 1;
            if (bv == null)
                return -1;
            if (typeof av === 'string') {
                return direction === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
            }
            if (typeof av === 'number') {
                return direction === 'asc' ? av - bv : bv - av;
            }
            return 0;
        });
    }
    return result;
};
const applyUserFilter = (data, userId, userRecipientRoles) => {
    if (!userId)
        return data;
    return data.filter((config) => config.additional_user_ids?.some((u) => u._id === userId) ||
        config.cc?.some((u) => u._id === userId) ||
        config.bcc?.some((u) => u._id === userId) ||
        (userRecipientRoles.length > 0 &&
            config.default_recipient?.some((r) => userRecipientRoles.includes(r))));
};
// Component
const EmailConfigurationMain = () => {
    const dispatch = useDispatch();
    const { setIsSidebarExpanded } = useLayout();
    // Collapse the sidebar submenu on visit (same behavior as Publisher Tagging).
    useEffect(() => {
        setIsSidebarExpanded(false);
    }, [setIsSidebarExpanded]);
    const configurations = useSelector(selectEmailConfigurations);
    const modules = useSelector(selectEmailConfigModules);
    const users = useSelector(selectEmailConfigUsers);
    const loading = useSelector(selectEmailConfigurationLoading);
    const saving = useSelector(selectEmailConfigurationSaving);
    // Build moduleNameMap once per modules change; used by filter/sort helpers.
    const moduleNameMap = useMemo(() => buildModuleNameMap(modules), [modules]);
    // Module filter dropdown shows only sub-modules and non-parent modules.
    const filterModuleNameMap = useMemo(() => buildFilterModuleNameMap(modules), [modules]);
    // Filter / search / sort state
    const [searchText, setSearchText] = useState("");
    const [selectedUser, setSelectedUser] = useState("");
    const [userRecipientRoles, setUserRecipientRoles] = useState([]);
    const [selectedRecipientType, setSelectedRecipientType] = useState("");
    const [selectedModule, setSelectedModule] = useState("");
    const [page, setPage] = useState(EMAIL_CONFIG_PAGINATION.DEFAULT_PAGE);
    const [sortConfig, setSortConfig] = useState({ columnId: null, direction: "asc" });
    // Table chrome state (column reorder + pinning + visibility)
    const [columnOrder, setColumnOrder] = useState(null);
    const [pinnedColumns, setPinnedColumns] = useState({ left: [], right: [] });
    const [hiddenColumns, setHiddenColumns] = useState(new Set());
    const [columnVisibilityAnchor, setColumnVisibilityAnchor] = useState(null);
    // User Assignment Drawer
    const [userAssignmentOpen, setUserAssignmentOpen] = useState(false);
    // Add/Edit Drawer
    const [addEditDrawerOpen, setAddEditDrawerOpen] = useState(false);
    const [addEditMode, setAddEditMode] = useState('add');
    const [selectedConfigForEdit, setSelectedConfigForEdit] = useState(null);
    // Delete confirmation
    const [deleteState, setDeleteState] = useState({
        open: false,
        loading: false,
        payload: null,
    });
    // Notes modal
    const [notesState, setNotesState] = useState({
        open: false,
        payload: null,
    });
    const isFirstLoad = useRef(true);
    // Single bootstrap call — replaces three separate fetches on mount.
    useEffect(() => {
        dispatch(fetchEmailConfigurationBootstrap());
    }, [dispatch]);
    useEffect(() => {
        if (configurations.length > 0 && isFirstLoad.current) {
            isFirstLoad.current = false;
        }
    }, [configurations]);
    // Fetch user's clientAccountHandlers roles when a user is selected in the filter.
    useEffect(() => {
        if (!selectedUser) {
            setUserRecipientRoles([]);
            return;
        }
        dispatch(fetchUserRecipientRoles(selectedUser))
            .unwrap()
            .then((roles) => setUserRecipientRoles(roles))
            .catch(() => setUserRecipientRoles([]));
    }, [selectedUser, dispatch]);
    // Filter + sort pipeline
    const filteredConfigurations = useMemo(() => {
        let result = applySearchAndSort(configurations, sortConfig, searchText, moduleNameMap);
        result = applyUserFilter(result, selectedUser, userRecipientRoles);
        if (selectedRecipientType) {
            result = result.filter((c) => c.default_recipient?.includes(selectedRecipientType));
        }
        if (selectedModule) {
            result = result.filter((c) => c.module_key === selectedModule);
        }
        return result;
    }, [configurations, sortConfig, searchText, moduleNameMap, selectedUser, userRecipientRoles, selectedRecipientType, selectedModule]);
    const totalPages = Math.ceil(filteredConfigurations.length / EMAIL_CONFIG_PAGINATION.LIMIT);
    const paginatedConfigurations = useMemo(() => {
        const start = (page - 1) * EMAIL_CONFIG_PAGINATION.LIMIT;
        return filteredConfigurations.slice(start, start + EMAIL_CONFIG_PAGINATION.LIMIT);
    }, [filteredConfigurations, page]);
    // Reset page when filters change
    useEffect(() => {
        setPage(EMAIL_CONFIG_PAGINATION.DEFAULT_PAGE);
    }, [searchText, sortConfig, selectedUser, selectedRecipientType, selectedModule]);
    // Handlers
    const resetFiltersAndPage = useCallback(() => {
        setSearchText("");
        setSelectedUser("");
        setSelectedRecipientType("");
        setSelectedModule("");
        setSortConfig({ columnId: null, direction: "asc" });
        setPage(EMAIL_CONFIG_PAGINATION.DEFAULT_PAGE);
    }, []);
    const handleAddClick = useCallback(() => {
        setAddEditMode('add');
        setSelectedConfigForEdit(null);
        setAddEditDrawerOpen(true);
    }, []);
    const handleEditModule = useCallback((config) => {
        setAddEditMode('edit');
        setSelectedConfigForEdit(config);
        setAddEditDrawerOpen(true);
    }, []);
    const handleDeleteModule = useCallback((config) => {
        setDeleteState({ open: true, loading: false, payload: config });
    }, []);
    const handleNotesModule = useCallback((config) => {
        setNotesState({ open: true, payload: config });
    }, []);
    const handleNotesClose = useCallback(() => {
        setNotesState({ open: false, payload: null });
    }, []);
    const handleSaveNotes = useCallback(async (notes) => {
        const config = notesState.payload;
        if (!config)
            return;
        await dispatch(updateEmailConfiguration({ id: config._id, notes })).unwrap();
    }, [notesState.payload, dispatch]);
    const handleAddEditDrawerClose = useCallback(() => {
        setAddEditDrawerOpen(false);
        setSelectedConfigForEdit(null);
    }, []);
    const handleConfirmDelete = useCallback(async () => {
        const config = deleteState.payload;
        if (!config)
            return;
        try {
            setDeleteState((p) => ({ ...p, loading: true }));
            await dispatch(deleteEmailConfiguration(config._id)).unwrap();
            setDeleteState({ open: false, loading: false, payload: null });
        }
        catch (error) {
            console.error('Failed to delete configuration:', error);
            setDeleteState((p) => ({ ...p, loading: false }));
        }
    }, [deleteState.payload, dispatch]);
    const handleSaveConfiguration = useCallback(async (data) => {
        try {
            const basePayload = {
                module_key: data.module_key,
                notification_key: data.notification_key,
                sender: data.sender,
                subject: data.subject,
                notes: data.notes,
                default_recipient: data.default_recipient,
                additional_user_ids: data.additional_user_ids,
                cc: data.cc,
                bcc: data.bcc,
            };
            if (addEditMode === 'edit' && data._id) {
                await dispatch(updateEmailConfiguration({ id: data._id, ...basePayload })).unwrap();
            }
            else {
                await dispatch(createEmailConfiguration(basePayload)).unwrap();
                resetFiltersAndPage();
            }
            setAddEditDrawerOpen(false);
            setSelectedConfigForEdit(null);
        }
        catch (error) {
            console.error('Failed to save configuration:', error);
        }
    }, [addEditMode, dispatch, resetFiltersAndPage]);
    const handleSaveUserAssignment = useCallback(async (updates) => {
        const bulkPayload = updates
            .map((u) => {
            const config = configurations.find((c) => c._id === u.configId);
            if (!config)
                return null;
            return {
                id: u.configId,
                module_key: u.module_key,
                notification_key: config.notification_key,
                sender: config.sender,
                subject: config.subject || '',
                default_recipient: config.default_recipient || [],
                additional_user_ids: u.additional_user_ids,
                cc: u.cc,
                bcc: u.bcc,
            };
        })
            .filter((u) => u !== null);
        if (bulkPayload.length === 0)
            return;
        try {
            await dispatch(bulkUpdateEmailConfigurations(bulkPayload)).unwrap();
        }
        catch (error) {
            console.error('Failed to bulk-update configurations:', error);
        }
    }, [configurations, dispatch]);
    const handleSortChange = useCallback((columnId, direction) => setSortConfig({ columnId, direction }), []);
    return (_jsxs(PageContainer, { children: [_jsx(Box, { className: "email-config-container", children: _jsxs(Paper, { className: "email-config-card", elevation: 0, children: [_jsx(EmailConfigHeader, { searchText: searchText, setSearchText: setSearchText, onAddClick: handleAddClick, onUsersClick: () => setUserAssignmentOpen(true), selectedUser: selectedUser, onUserChange: setSelectedUser, selectedRecipientType: selectedRecipientType, onRecipientTypeChange: setSelectedRecipientType, selectedModule: selectedModule, onModuleChange: setSelectedModule, moduleNameMap: filterModuleNameMap, users: users, onColumnVisibilityClick: (e) => setColumnVisibilityAnchor(e.currentTarget) }), _jsx(Box, { className: "email-config-table-wrapper", children: _jsx(EmailConfigList, { configurations: paginatedConfigurations, moduleNameMap: moduleNameMap, loading: loading, isFirstLoad: isFirstLoad.current, searchText: searchText, onEditModule: handleEditModule, onDeleteModule: handleDeleteModule, onNotesModule: handleNotesModule, sortConfig: sortConfig, onSortChange: handleSortChange, columnOrder: columnOrder, onColumnOrderChange: setColumnOrder, pinnedColumns: pinnedColumns, onColumnPinningChange: setPinnedColumns, hiddenColumns: hiddenColumns, onColumnVisibilityChange: setHiddenColumns, columnVisibilityAnchor: columnVisibilityAnchor, onColumnVisibilityClose: () => setColumnVisibilityAnchor(null) }) }), filteredConfigurations.length > EMAIL_CONFIG_PAGINATION.LIMIT && (_jsx(Box, { className: "email-config-pagination-wrapper", children: _jsx(Pagination, { currentPage: page, totalPages: totalPages, onPageChange: setPage, maxVisiblePages: 5, totalEntries: filteredConfigurations.length, itemsPerPage: EMAIL_CONFIG_PAGINATION.LIMIT, showEntriesMessage: true }) }))] }) }), _jsx(UserAssignmentDrawer, { open: userAssignmentOpen, onClose: () => setUserAssignmentOpen(false), allUsers: users, configurations: configurations, modules: modules, onSave: handleSaveUserAssignment, saving: saving }), _jsx(AddEditDrawer, { open: addEditDrawerOpen, onClose: handleAddEditDrawerClose, configuration: selectedConfigForEdit, modules: modules, allUsers: users, onSave: handleSaveConfiguration, saving: saving, mode: addEditMode }), deleteState?.open && (_jsx(ConfirmActionModal, { className: "delete-popup-container", open: deleteState.open, loading: deleteState.loading, title: "Are You Sure?", description: "You're about to delete this email configuration. This action cannot be undone.", confirmText: "Yes, Proceed", cancelText: "No, Cancel", onClose: () => !deleteState.loading && setDeleteState({ open: false, loading: false, payload: null }), onConfirm: handleConfirmDelete })), notesState.open && (_jsx(NotesModal, { className: "email-configuration-notes-paper", open: notesState.open, onClose: handleNotesClose, initialNotes: notesState.payload?.notes || "", onSave: handleSaveNotes, actionLoading: saving }))] }));
};
export default EmailConfigurationMain;
